import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ReactTyped } from 'react-typed';
import { Link } from 'react-router-dom';
import { 
  FaGithub, 
  FaLinkedin, 
  FaFacebook, 
  FaCode, 
  FaReact, 
  FaNodeJs,
  FaDatabase,
  FaServer,
  FaHtml5,
  FaCss3Alt,
  FaJsSquare,
  FaGitAlt,
  FaPhp
} from 'react-icons/fa';
import { 
  SiMongodb, 
  SiExpress, 
  SiNextdotjs, 
  SiTypescript,
  SiTailwindcss,
  SiMysql,
  SiLaravel,
  SiBootstrap
} from 'react-icons/si';
import { HiOutlineMail } from 'react-icons/hi';
import SEO from '../components/SEO';
import { scrollToElement } from '../services/navigation';
import profilePic from '../assets/my-profile.jpg';


const Home: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const stats = [
    { number: '3+', label: 'Years Experience' },
    { number: '25+', label: 'Projects Completed' },
    { number: '15+', label: 'Happy Clients' },
    { number: '10+', label: 'Technologies' },
  ];

  const techStack = [
    { name: 'HTML5', icon: FaHtml5, color: '#E34F26' },
    { name: 'CSS3', icon: FaCss3Alt, color: '#1572B6' },
    { name: 'JavaScript', icon: FaJsSquare, color: '#F7DF1E' },
    { name: 'Bootstrap', icon: SiBootstrap, color: '#7952B3' },
    { name: 'Tailwind CSS', icon: SiTailwindcss, color: '#06B6D4' },
    { name: 'PHP', icon: FaPhp, color: '#777BB4' },
    { name: 'Laravel', icon: SiLaravel, color: '#FF2D20' },
    { name: 'MySQL', icon: SiMysql, color: '#4479A1' },
    { name: 'React.js', icon: FaReact, color: '#61DAFB' },
    { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
    { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
    { name: 'Node.js', icon: FaNodeJs, color: '#339933' },
    { name: 'Express.js', icon: SiExpress, color: '#000000' },
    { name: 'MongoDB', icon: SiMongodb, color: '#47A248' },
    { name: 'Git', icon: FaGitAlt, color: '#F05032' },
    { name: 'GitHub', icon: FaGithub, color: '#181717' },
    { name: 'Postman', icon: FaCode, color: '#FF6C37' },
  ];

  const skills = [
    { name: 'Frontend Development', icon: FaCode, color: '#6C63FF', desc: 'React.js, Next.js, Tailwind CSS' },
    { name: 'Backend Development', icon: FaServer, color: '#FF6584', desc: 'Node.js, Express.js, PHP' },
    { name: 'Database Design', icon: FaDatabase, color: '#6C63FF', desc: 'MongoDB, MySQL, Schema Design' },
    { name: 'Full Stack Architecture', icon: FaReact, color: '#FF6584', desc: 'MERN Stack, PHP/MySQL' },
  ];

  return (
    <>
      <SEO 
        title="Home" 
        description="Sarfraz Ahmed - Professional Full Stack Web Developer with 3+ years experience in MERN Stack, PHP, and MySQL" 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center">
          <div className="container-custom px-4 md:px-8 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Profile Image */}
              <motion.div
                ref={ref}
                initial={{ opacity: 0, x: -50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="flex justify-center lg:justify-start"
              >
                <div className="relative">
                  {/* Decorative Circle */}
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl" />
                  <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-gradient-to-r from-secondary/20 to-primary/20 rounded-full blur-3xl" />
                  
                  {/* Profile Image */}
                  <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-primary/20">
                    <img
                    src={profilePic}  // ✅ LOCAL IMAGE FROM ASSETS FOLDER
                      alt="Sarfraz Ahmed"
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
                  </div>

                  {/* Floating Badge */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                    className="absolute -bottom-4 -right-4 bg-white dark:bg-gray-900 rounded-xl shadow-xl px-4 py-2 flex items-center gap-2"
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Available for Work</span>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center lg:text-left"
              >
                {/* Greeting */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 }}
                  className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4"
                >
                  👋 Welcome to my portfolio
                </motion.div>

                {/* Name */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-gray-800 dark:text-gray-100 mb-2"
                >
                  Sarfraz Ahmed
                </motion.h1>

                {/* Typing Animation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 }}
                  className="text-xl md:text-2xl lg:text-3xl font-medium text-gray-600 dark:text-gray-300 mb-4"
                >
                  <span className="text-primary font-bold">
                    <ReactTyped
                      strings={[
                        'Full-Stack Web Developer',
                        'MERN Stack Developer',
                        'NEXT JS Developer',
                        'PHP Developer',
                        'UI/UX Designer',
                        'Database Administrator',
                      ]}
                      typeSpeed={50}
                      backSpeed={30}
                      loop
                    />
                  </span>
                </motion.div>

                {/* Bio - Updated with your info */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 }}
                  className="text-gray-600 dark:text-gray-300 max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed"
                >
                  Building scalable, secure, and performant web applications with MERN Stack, 
                  PHP/MySQL, and modern cloud technologies. 3+ years of experience in full-stack development.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.7 }}
                  className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/contact"
                      className="block px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Hire Me
                    </Link>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button
                      type="button"
                      onClick={() => scrollToElement('tech-stack')}
                      className="px-8 py-3 bg-white dark:bg-gray-800 text-primary rounded-xl font-medium shadow-md hover:shadow-xl transition-all duration-300 border-2 border-primary/20"
                    >
                      View Tech Stack
                    </button>
                  </motion.div>
                </motion.div>

                {/* Social Icons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 }}
                  className="flex gap-4 justify-center lg:justify-start"
                >
                  {[
                    { icon: FaGithub, href: 'https://github.com/sheru-project', label: 'GitHub' },
                    { icon: FaLinkedin, href: 'https://www.linkedin.com/in/sarfrazahmed1107/', label: 'LinkedIn' },
                    { icon: FaFacebook, href: 'https://www.facebook.com/sherubhai1107', label: 'Facebook' },
                    { icon: HiOutlineMail, href: 'mailto:listenrhymes@gmail.com', label: 'Email' },
                  ].map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-white dark:bg-gray-900 rounded-xl flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primary shadow-md hover:shadow-xl transition-all duration-300 hover:scale-110"
                      whileHover={{ y: -5 }}
                      aria-label={social.label}
                    >
                      <social.icon className="text-xl" />
                    </motion.a>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section - Updated with your stats */}
        <section className="py-12 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
          <div className="container-custom px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6 bg-white dark:bg-gray-900 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="text-3xl md:text-4xl font-display font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-1">
                    {stat.number}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack Section */}
        <section id="tech-stack" className="py-16">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Tech <span className="text-gray-800 dark:text-gray-100">Stack</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                Technologies I work with to build amazing applications
              </p>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {techStack.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 hover:rotate-1 text-center">
                    <div 
                      className="text-3xl md:text-4xl mx-auto mb-2 transition-transform duration-300 group-hover:scale-110"
                      style={{ color: tech.color }}
                    >
                      <tech.icon />
                    </div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-300">{tech.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Skills Cards - Updated with your skills */}
        <section id="skills" className="py-16 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950">
          <div className="container-custom px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-3">
                Core <span className="text-gray-800 dark:text-gray-100">Skills</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                What I bring to the table
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {skills.map((skill, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 text-center border-b-4 border-primary/20 hover:border-primary">
                    <div 
                      className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${skill.color}15` }}
                    >
                      <skill.icon className="text-3xl" style={{ color: skill.color }} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">{skill.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{skill.desc}</p>
                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mt-3" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;